import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ActivationPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const ActivationSuccessPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('checking'); // 'checking', 'ready', 'timeout'
    const pollCount = useRef(0);
    const maxPolls = 15; // 15 polls × 2s = 30 seconds timeout

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                pollCount.current += 1;
                const res = await axios.get(`${API_BASE_URL}/activate/${token}`);

                // If the link is now "used", the account has been created
                if (res.data?.data === undefined && res.response?.status === 410) {
                    setStatus('ready');
                    clearInterval(interval);
                }
            } catch (err) {
                // 410 status means the link was used (account created)
                if (err.response?.status === 410 && err.response?.data?.errorType === 'used') {
                    setStatus('ready');
                    clearInterval(interval);
                }
            }

            if (pollCount.current >= maxPolls) {
                setStatus('timeout');
                clearInterval(interval);
            }
        }, 2000);

        return () => clearInterval(interval);
    }, [token]);

    // Auto-redirect when account is ready
    useEffect(() => {
        if (status === 'ready') {
            const timer = setTimeout(() => {
                navigate('/login?activated=true');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [status, navigate]);

    return (
        <div className="activation-page">
            <div className="activation-container">
                <div className="activation-header">
                    <div className="activation-logo">
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                            <rect width="40" height="40" rx="10" fill="#6366f1" />
                            <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fill="white" fontSize="18" fontWeight="700">E</text>
                        </svg>
                        <span className="activation-logo-text">Envion</span>
                    </div>
                </div>

                <div className="activation-success-card">
                    {status === 'checking' && (
                        <>
                            <div className="activation-success-icon">
                                <svg className="activation-checkmark-anim" width="80" height="80" viewBox="0 0 80 80">
                                    <circle cx="40" cy="40" r="36" fill="none" stroke="#6366f1" strokeWidth="3" strokeDasharray="226" strokeDashoffset="226" className="activation-check-circle" />
                                </svg>
                                <div className="activation-spinner activation-spinner-lg" />
                            </div>
                            <h2>Payment Successful!</h2>
                            <p className="activation-success-subtitle">Setting up your account...</p>
                            <div className="activation-progress-bar">
                                <div className="activation-progress-fill" />
                            </div>
                            <p className="activation-success-note">This usually takes just a few seconds.</p>
                        </>
                    )}

                    {status === 'ready' && (
                        <>
                            <div className="activation-success-icon activation-success-ready">
                                <svg width="80" height="80" viewBox="0 0 80 80">
                                    <circle cx="40" cy="40" r="36" fill="#10b981" opacity="0.1" />
                                    <circle cx="40" cy="40" r="36" fill="none" stroke="#10b981" strokeWidth="3" />
                                    <path d="M25 40 L35 50 L55 30" fill="none" stroke="#10b981" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h2>Your Account is Ready! 🎉</h2>
                            <p className="activation-success-subtitle">
                                You can now log in with your email and the password you created.
                            </p>
                            <button
                                className="activation-btn-primary activation-btn-lg"
                                onClick={() => navigate('/login?activated=true')}
                            >
                                Go to Login
                            </button>
                            <p className="activation-success-note">Redirecting automatically in 3 seconds...</p>
                        </>
                    )}

                    {status === 'timeout' && (
                        <>
                            <div className="activation-success-icon">
                                <svg width="80" height="80" viewBox="0 0 80 80">
                                    <circle cx="40" cy="40" r="36" fill="#f59e0b" opacity="0.1" />
                                    <circle cx="40" cy="40" r="36" fill="none" stroke="#f59e0b" strokeWidth="3" />
                                    <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fill="#f59e0b" fontSize="36">⏳</text>
                                </svg>
                            </div>
                            <h2>Taking a Bit Longer</h2>
                            <p className="activation-success-subtitle">
                                Your payment was successful but account setup is taking longer than expected.
                                You should receive access shortly.
                            </p>
                            <p className="activation-success-note">
                                If you don't receive access within a few minutes, please contact{' '}
                                <a href="mailto:support@envion.co.uk">support@envion.co.uk</a>
                            </p>
                            <button
                                className="activation-btn-secondary"
                                onClick={() => navigate('/login')}
                            >
                                Try Logging In
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ActivationSuccessPage;
